import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import {
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import { RecallPermissionKind } from "@/modules/electron/types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  advancedMeetingPermissionsRequired: boolean;
  restartRequired: boolean;
  onRestart: () => void;
};

export const MeetingPermissionsModalContent: FC<Props> = ({
  isOpen,
  onClose,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
  onRestart,
  restartRequired,
  advancedMeetingPermissionsRequired,
}) => {
  const items = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Permission,
        id: "accessibility",
        title: "Accessibility",
        description: "Mem relies on accessibility features to detect when you've joined a meeting",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Accessibility,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Accessibility,
          }),
      },
    ];

    if (advancedMeetingPermissionsRequired) {
      itemList.push({
        kind: ConfigurationListItemKind.Divider,
        id: "divider-full-disk-access",
      });

      itemList.push({
        kind: ConfigurationListItemKind.Permission,
        id: "full-disk-access",
        title: "Full disk access",
        description:
          "Mem relies on disk access functionality to improve detection of Microsoft Teams meetings",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.FullDiskAccess }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.FullDiskAccess,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.FullDiskAccess,
          }),
      });
    }

    if (restartRequired) {
      itemList.push({
        kind: ConfigurationListItemKind.Divider,
        id: "divider-restart",
      });

      itemList.push({
        kind: ConfigurationListItemKind.Button,
        id: "restart",
        title: "Restart Mem",
        description: "To complete setup, restart Mem.",
        actionLabel: "Restart",
        variant: MDSButtonVariant.Contrast,
        onClick: onRestart,
      });
    }

    return itemList;
  }, [
    handleEnablePermission,
    handleOpenPermissionPane,
    onRestart,
    resolveConfigurationStatus,
    restartRequired,
    advancedMeetingPermissionsRequired,
  ]);

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <Header slot={MDSDialogSlotName.Header}>
        <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
          Enable meeting notifications
        </MDSText>
      </Header>

      <DialogContent slot={MDSDialogSlotName.Content}>
        <Description variant={MDSTextVariant.ParagraphS}>
          Mem will let you start voice mode with a single click any time a meeting is detected
        </Description>

        <ConfigurationList items={items} />
      </DialogContent>
    </MDSModalDialog>
  );
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: ${({ theme }) => theme.spacing["2"]};
`;

const DialogContent = styled(MDSDialogTextContent)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["6"]};
`;

const Description = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;
